'use client';

import { c as _c } from "react/compiler-runtime";
import React from 'react';
import { ClpCourseProvider, TrackedCourse } from './context/clp-course-provider.react-component';
import { CLPPurchaseContextProvider } from './context/clp-purchase-context.react-component';
import { DiscountCodeProvider, useDiscountCodeProvider } from './context/discount-code-provider.react-component';
import { ClpContextProvider } from './context/clp-context-provider.react-component';
import { CoursePriceProvider } from '@udemy-v2/react-course-pricing';
import { CourseWithInstructorDetailsProvider } from './context/course-with-instructor-details-provider.react-component';
import { PriceEventTrackingProvider } from '@udemy-v2/react-currency-formatting';
import { GwgAccelerator } from '@udemy-v2/career-accelerators';
import { CourseCareerAcceleratorProvider } from './context/career-accelerator-provider.react-component';
import { CLPVoucherContextProvider } from './context/voucher-context.react-component';
import { AllGwgAcceleratorsContext } from './context/all-gwg-accelerators-context';
import { CareerAcceleratorsByCourseIds } from '@udemy-v2/career-accelerators';
import { CourseLandingPage } from './course-landing-page.react-component';
import { CourseSubscriptionPlansProvider, SubscriptionPlanProductType, SubscriptionPlansProvider } from '@udemy-v2/subscription-graphql';
import { CourseSeoPricing } from '@/features/course-landing-page/components/course-seo/course-seo-pricing.react-component';
import { CoursePreviewVideoProvider } from '@/features/course-landing-page/components/course-preview/context';
import { SmartBarStoreProvider } from '@udemy-v2/react-smart-bar';
import { AssetImageProvider } from './context/asset-image-provider.react-component';
import { AssetImage } from './components/course-preview-entry-point/course-preview-entry-point.react-component';
import { ClpSubscriptionContextProvider } from './context/clp-subscription-context.react-component';
import { ClpNoticeContextProvider } from './context/clp-notice-context-provider.react-component';
import { ClpGtmDataLayer } from './components/google-tag-manager/clp-gtm-data-layer.react-component';
import { useFeatures } from '@udemy-v2/react-experimentation';
import { FbtBundleConfigProvider } from './context/fbt-bundle-config-provider.react-component';

/**
 * Subscription plan product types relevant for CLP GrowWithGoogle offerings
 */
const CLP_GROW_WITH_GOOGLE_SUBSCRIPTION_PLAN_TYPES = [SubscriptionPlanProductType.GrowWithGoogle, SubscriptionPlanProductType.ConsumersubscriptionPlusGrowWithGoogle];
interface CourseLandingPageWithProvidersProps {
  course: TrackedCourse;
  courseCareerAccelerator?: CareerAcceleratorsByCourseIds;
  allGwgAccelerators?: GwgAccelerator[];
}

/**
 * Wrapper component that passes discountCodeProvider to CoursePriceProvider
 */
function CoursePriceProviderWithDiscount(t0) {
  const $ = _c(3);
  const {
    children
  } = t0;
  const {
    discountCodeProvider
  } = useDiscountCodeProvider();
  let t1;
  if ($[0] !== children || $[1] !== discountCodeProvider) {
    t1 = <CoursePriceProvider discountCodeProvider={discountCodeProvider}>{children}</CoursePriceProvider>;
    $[0] = children;
    $[1] = discountCodeProvider;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return t1;
}

/**
 * Inner component that sets up the provider hierarchy
 */
function CourseLandingPageWithCoursePriceProvider(props) {
  const $ = _c(40);
  const {
    course,
    courseCareerAccelerator,
    allGwgAccelerators
  } = props;
  let t0;
  if ($[0] !== course.images) {
    t0 = <AssetImage images={course.images} />;
    $[0] = course.images;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const preloadedAssetImage = t0;
  const marketingURL = typeof window !== "undefined" ? window.location.href : "https://www.udemy.com";
  let t1;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = [...CLP_GROW_WITH_GOOGLE_SUBSCRIPTION_PLAN_TYPES.map(productType => ({
      filters: {
        productType
      },
      marketingURL
    })), {
      filters: {
        productType: SubscriptionPlanProductType.Consumersubscription
      },
      marketingURL
    }];
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const subscriptionPlanQueryVariables = t1;
  let t2;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = ["blended_monetization_promo"];
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const {
    featureVariantAssignments
  } = useFeatures(t2);
  const blendedMonthlyEnabled = (featureVariantAssignments.get("blended_monetization_promo")?.configuration?.blended_monthly_enabled ?? false) as boolean;
  let t3;
  if ($[4] !== featureVariantAssignments) {
    t3 = featureVariantAssignments.get("blended_monetization_promo")?.configuration?.monthly_discount_code;
    $[4] = featureVariantAssignments;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const blendedMonthlyDiscountCode = t3 as string | undefined;
  let t4;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      productType: SubscriptionPlanProductType.Consumersubscription
    };
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const t5 = blendedMonthlyEnabled ? blendedMonthlyDiscountCode : undefined;
  let t6;
  if ($[7] !== t5) {
    t6 = {
      filters: t4,
      marketingURL,
      couponCode: t5
    };
    $[7] = t5;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  const courseSubscriptionPlanQueryVariables = t6;
  const t7 = String(course.id);
  const t8 = course.urlCourseLanding ?? "";
  const t9 = String(course.id);
  let t10;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <ClpGtmDataLayer />;
    $[9] = t10;
  } else {
    t10 = $[9];
  }
  let t11;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = {
      context: "landing-page"
    };
    $[10] = t11;
  } else {
    t11 = $[10];
  }
  let t12;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = {
      forceNoticeType: "smart_bar",
      showSmartBar: true
    };
    $[11] = t12;
  } else {
    t12 = $[11];
  }
  let t13;
  if ($[12] !== course) {
    t13 = <CourseSeoPricing course={course} />;
    $[12] = course;
    $[13] = t13;
  } else {
    t13 = $[13];
  }
  let t14;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <CourseLandingPage />;
    $[14] = t14;
  } else {
    t14 = $[14];
  }
  let t15;
  if ($[15] !== t13) {
    t15 = <SmartBarStoreProvider config={t12} data={null}>{t13}{t14}</SmartBarStoreProvider>;
    $[15] = t13;
    $[16] = t15;
  } else {
    t15 = $[16];
  }
  let t16;
  if ($[17] !== course.instructors || $[18] !== t15) {
    t16 = <CoursePreviewVideoProvider><ClpContextProvider><PriceEventTrackingProvider priceEventTrackingContext={t11}><CourseWithInstructorDetailsProvider instructors={course.instructors}>{t15}</CourseWithInstructorDetailsProvider></PriceEventTrackingProvider></ClpContextProvider></CoursePreviewVideoProvider>;
    $[17] = course.instructors;
    $[18] = t15;
    $[19] = t16;
  } else {
    t16 = $[19];
  }
  let t17;
  if ($[20] !== preloadedAssetImage || $[21] !== t16) {
    t17 = <DiscountCodeProvider><CoursePriceProviderWithDiscount>{t10}<CLPPurchaseContextProvider><CLPVoucherContextProvider><AssetImageProvider preloadedAssetImage={preloadedAssetImage}>{t16}</AssetImageProvider></CLPVoucherContextProvider></CLPPurchaseContextProvider></CoursePriceProviderWithDiscount></DiscountCodeProvider>;
    $[20] = preloadedAssetImage;
    $[21] = t16;
    $[22] = t17;
  } else {
    t17 = $[22];
  }
  let t18;
  if ($[23] !== courseCareerAccelerator || $[24] !== t17) {
    t18 = <CourseCareerAcceleratorProvider courseCareerAccelerator={courseCareerAccelerator}>{t17}</CourseCareerAcceleratorProvider>;
    $[23] = courseCareerAccelerator;
    $[24] = t17;
    $[25] = t18;
  } else {
    t18 = $[25];
  }
  let t19;
  if ($[26] !== allGwgAccelerators || $[27] !== t18) {
    t19 = <AllGwgAcceleratorsContext.Provider value={allGwgAccelerators}>{t18}</AllGwgAcceleratorsContext.Provider>;
    $[26] = allGwgAccelerators;
    $[27] = t18;
    $[28] = t19;
  } else {
    t19 = $[28];
  }
  let t20;
  if ($[29] !== course || $[30] !== t19) {
    t20 = <ClpNoticeContextProvider><ClpCourseProvider course={course}>{t19}</ClpCourseProvider></ClpNoticeContextProvider>;
    $[29] = course;
    $[30] = t19;
    $[31] = t20;
  } else {
    t20 = $[31];
  }
  let t21;
  if ($[32] !== t20 || $[33] !== t9) {
    t21 = <ClpSubscriptionContextProvider courseId={t9}>{t20}</ClpSubscriptionContextProvider>;
    $[32] = t20;
    $[33] = t9;
    $[34] = t21;
  } else {
    t21 = $[34];
  }
  let t22;
  if ($[35] !== courseSubscriptionPlanQueryVariables || $[36] !== t21 || $[37] !== t7 || $[38] !== t8) {
    t22 = <FbtBundleConfigProvider><SubscriptionPlansProvider subscriptionPlanQueryVariables={subscriptionPlanQueryVariables}><CourseSubscriptionPlansProvider courseId={t7} courseLandingUrl={t8} subscriptionPlanQueryVariables={courseSubscriptionPlanQueryVariables}>{t21}</CourseSubscriptionPlansProvider></SubscriptionPlansProvider></FbtBundleConfigProvider>;
    $[35] = courseSubscriptionPlanQueryVariables;
    $[36] = t21;
    $[37] = t7;
    $[38] = t8;
    $[39] = t22;
  } else {
    t22 = $[39];
  }
  return t22;
}
export function CourseLandingPageWithProviders(props) {
  const $ = _c(2);
  let t0;
  if ($[0] !== props) {
    t0 = <CourseLandingPageWithCoursePriceProvider {...props} />;
    $[0] = props;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return t0;
}